import { HOME_FAQ } from "@/components/site/faq-data";
import { SERVICE_CATALOG } from "@/components/site/services-catalog";
import { EMAIL } from "@/components/site/primitives";
import { SITE_DESCRIPTION, SITE_ORIGIN, SOCIAL_IMAGE } from "@/lib/seo";
import type { ReactNode } from "react";
import { isValidElement } from "react";

const BUSINESS_ID = `${SITE_ORIGIN}/#business`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;

function toPlainText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(toPlainText).join("");
  if (isValidElement(node)) {
    return toPlainText((node.props as { children?: ReactNode }).children);
  }
  return "";
}

export const LOCAL_BUSINESS = {
  "@type": ["RoofingContractor", "HomeAndConstructionBusiness"],
  "@id": BUSINESS_ID,
  name: "SeaClay Roofing & Spray Foam Insulation LLC",
  alternateName: "SeaClay",
  url: SITE_ORIGIN,
  image: SOCIAL_IMAGE,
  logo: `${SITE_ORIGIN}/favicon.png`,
  telephone: "+1-715-418-3887",
  email: EMAIL,
  founder: {
    "@type": "Person",
    name: "Gary Risler",
  },
  description: SITE_DESCRIPTION,
  priceRange: "$$",
  currenciesAccepted: "USD",
  paymentAccepted: "Cash, Check",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Prairie Farm",
    addressRegion: "WI",
    postalCode: "54762",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 45.2369,
    longitude: -91.9804,
  },
  areaServed: {
    "@type": "AdministrativeArea",
    name: "Northwest Wisconsin",
  },
  knowsAbout: [
    "Spray foam insulation",
    "New construction spray foam",
    "Blown insulation",
    "Batting insulation",
    "Spray foam roofing",
    "Metal roof restoration",
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Insulation and roofing services",
    itemListElement: SERVICE_CATALOG.map((service) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: service.title,
        description: service.copy,
        url: `${SITE_ORIGIN}/services/${service.slug}`,
      },
    })),
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+1-715-418-3887",
      contactType: "sales",
      areaServed: "US-WI",
      availableLanguage: ["English"],
    },
  ],
} as const;

export function websiteJsonLd() {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: SITE_ORIGIN,
    name: "SeaClay Roofing & Spray Foam Insulation",
    description: SITE_DESCRIPTION,
    publisher: { "@id": BUSINESS_ID },
    inLanguage: "en-US",
  };
}

export function faqJsonLd(items: { q: string; a: ReactNode }[] = HOME_FAQ) {
  return {
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: toPlainText(item.a),
      },
    })),
  };
}

export function homeJsonLd() {
  return {
    "@context": "https://schema.org",
    "@graph": [LOCAL_BUSINESS, websiteJsonLd(), faqJsonLd()],
  };
}

export function serviceJsonLd(input: { title: string; description: string; slug: string }) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      LOCAL_BUSINESS,
      {
        "@type": "Service",
        name: input.title,
        description: input.description,
        url: `${SITE_ORIGIN}/services/${input.slug}`,
        provider: { "@id": BUSINESS_ID },
        areaServed: "Northwest Wisconsin",
        brand: {
          "@type": "Brand",
          name: "SeaClay Roofing & Spray Foam Insulation",
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: SITE_ORIGIN,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: `${SITE_ORIGIN}/services`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: input.title,
            item: `${SITE_ORIGIN}/services/${input.slug}`,
          },
        ],
      },
    ],
  };
}

export function servicesIndexJsonLd() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      LOCAL_BUSINESS,
      {
        "@type": "CollectionPage",
        name: "Insulation and roofing services",
        url: `${SITE_ORIGIN}/services`,
        mainEntity: {
          "@type": "ItemList",
          itemListElement: SERVICE_CATALOG.map((service, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: service.title,
            url: `${SITE_ORIGIN}/services/${service.slug}`,
          })),
        },
      },
    ],
  };
}

export { toPlainText, BUSINESS_ID };
